/** Every enforcement level, most enforced first, with its label. @public */
export const ENFORCEMENT_LEVELS = Object.freeze({
  enforced: { label: "Enforced" },
  "self-attested": { label: "Self-attested" },
  none: { label: "Not enforced" },
} as const);

/** @public */
export type EnforcementLevel = keyof typeof ENFORCEMENT_LEVELS;

/** The levels, most enforced first. @public */
export const ENFORCEMENT_LEVEL_IDS = Object.keys(
  ENFORCEMENT_LEVELS
) as unknown as readonly [EnforcementLevel, ...EnforcementLevel[]];

/** @public */
export type EnforcementLabel =
  (typeof ENFORCEMENT_LEVELS)[EnforcementLevel]["label"];

/** @public */
export function enforcementOf(raw: unknown): EnforcementLevel {
  return (ENFORCEMENT_LEVEL_IDS as ReadonlyArray<unknown>).includes(raw)
    ? (raw as EnforcementLevel)
    : "none";
}

/** The label of the metadata's enforcement level. @public */
export function enforcementLabel(
  metadata: { enforcement?: unknown } | undefined
): EnforcementLabel {
  return ENFORCEMENT_LEVELS[enforcementOf(metadata?.enforcement)].label;
}

/**
 * A standard's level from its enforcement mechanisms: `ci` enforces it, any
 * other mechanism leaves it self-attested. A scorecard check never raises it.
 * @public
 */
export function enforcementOfMechanisms(
  mechanisms: ReadonlyArray<string>
): Exclude<EnforcementLevel, "none"> {
  return mechanisms.some((m) => m.toLocaleLowerCase("en-US") === "ci")
    ? "enforced"
    : "self-attested";
}
